import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
  ChartData,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { differenceInDays } from 'date-fns';
import { generateDateLabels, generateChartData } from '../utils/assessmentProgress';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

interface DepartmentResponse {
  date: string;
  count: number;
}

interface AssessmentProgressData {
  department_responses_by_date: DepartmentResponse[];
}

interface DashboardAssessmentProgressProps {
  assessment_progress_data: AssessmentProgressData;
  start_date: Date | string;
  end_date: Date | string;
  height?: string | number;
}

const DashboardAssessmentProgressRight: React.FC<DashboardAssessmentProgressProps> = ({
  assessment_progress_data,
  start_date,
  end_date,
  height,
}) => {
  const dateLabels: string[] = generateDateLabels(start_date, end_date);
  const chartData: number[] = generateChartData(assessment_progress_data, start_date, end_date);

  const data: ChartData<'bar'> = {
    labels: dateLabels,
    datasets: [
      {
        label: 'Total Assessments Completed',
        data: chartData,
        backgroundColor: 'rgba(129, 199, 132, 1)',
      },
    ],
  };

  const options: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        display: false,
        labels: {
          font: {
            size: 12,
            family: 'Poppins',
          },
          boxWidth: 10,
          boxHeight: 10,
        },
      },

      title: {
        display: false,
        text: 'Survey Responses by Department',
      },
    },
    scales: {
      x: {
        stacked: true,
        title: {
          display: false,
          text: 'Dates',
          font: {
            family: 'Poppins',
            size: 11,
          },
        },
        ticks: {
          // @ts-ignore
          callback: function (this: any, index: number): string {
            const rawLabel = this.chart.data.labels[index];
            const dateObj = new Date(rawLabel);
            if (isNaN(dateObj.getTime())) return rawLabel;
            return dateObj.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              timeZone: 'UTC',
            });
          },
        },
      },
      y: {
        stacked: true,
        title: {
          display: false,
          text: 'Assessments Completed',
          font: {
            family: 'Poppins',
            size: 12,
          },
        },

        ticks: {
          stepSize: 1,
        },
        min: 0,
        // @ts-ignore
        max: (ctx: { chart: { data: { datasets: { data: number[] }[] } } }): number => {
          const maxValue = Math.max(...ctx.chart.data.datasets[0].data);
          return Math.ceil(maxValue < 10 ? 10 : maxValue); // Ensures a number is always returned
        },
      },
    },
  };

  return (
    <div style={{ height: `${height}`, width: '100%' } as React.CSSProperties}>
      <Bar data={data} options={options} />
    </div>
  );
};

export default DashboardAssessmentProgressRight;
